import React, { createContext, useState, useCallback, useMemo, useEffect } from 'react';

import {
  crmLoginAPI,
  changePasswordAPI,
  logoutAPI,
  verifyPortalOtpAPI,
  resendPortalOtpAPI,
} from 'Api/portalAuthAPI';
import { onSessionExpired, onSessionRefreshed, keepSessionFresh } from 'Api/portalClient';
import {
  savePortalSession,
  loadPortalSession,
  clearPortalSession,
} from 'helpers/portalSession';
import { logSessionIdentity } from 'helpers/sessionIdentityLog';
import { permissionChecker } from 'helpers/permissions';
import { purgeActingPartnerStorage } from './actingPartnerStorage';

const PORTAL = 'crm';

export const AuthContext = createContext(null);

/**
 * Zenvoya CRM authentication.
 *
 * Signs in against POST /app/v1/crm/auth/login, which authenticates
 * `partner_users` and admits ZENVOYA_ROOT / ZENVOYA_B2C accounts. This is a
 * different credential store from the legacy /auth/adminLogin flow in
 * views/auth/SignIn.jsx (that one targets the `users` table and adds an emailed
 * OTP); the two are not interchangeable.
 *
 * Which portal a user may enter is decided by the SERVER from their partner's
 * type — nothing in the request selects it — so a partner user hitting this
 * screen is refused by the backend rather than by a check here.
 *
 * State is seeded from sessionStorage so a refresh does not sign the user out.
 */
export function AuthProvider({ children }) {
  // Lazy initialiser: read storage once at mount rather than on every render.
  const [session, setSession] = useState(() => loadPortalSession(PORTAL));

  const [loggingIn, setLoggingIn] = useState(false);

  /**
   * The pre-auth token from MFA step one, in MEMORY only — never sessionStorage.
   * It lives ten minutes, is accepted on exactly two routes, and a reload should
   * drop a half-finished sign-in back to the start rather than resume it.
   */
  const [preAuthToken, setPreAuthToken] = useState('');
  const [mfaEmail, setMfaEmail] = useState('');

  /**
   * The password just used to sign in, in MEMORY only, so the change-password
   * screen can rotate it without asking the user to retype what they typed one
   * screen ago. Cleared as soon as it is no longer needed — on rotation, on
   * sign-out, and on any login that does not demand one. A reload loses it like
   * it loses `preAuthToken`, and `changePassword` reports that rather than
   * silently asking for the temporary password again.
   */
  const [pendingPassword, setPendingPassword] = useState('');

  const login = useCallback(async (email, password) => {
    // Trim BEFORE the blank check: an address of nothing but spaces is truthy,
    // so checking first would send an empty email for the server to reject
    // instead of telling the user the field is empty.
    const trimmedEmail = String(email ?? '').trim();
    if (!trimmedEmail || !password) {
      return { ok: false, message: 'Enter your email and password.' };
    }

    setLoggingIn(true);
    try {
      const result = await crmLoginAPI(trimmedEmail, password);
      if (!result.ok) return result;

      /**
       * With MFA on, step one returns a pre-auth token and NO session. Nothing is
       * stored — a half-authenticated state in sessionStorage would present the
       * portal as signed in after a reload.
       */
      if (result.data?.mfaRequired) {
        setPreAuthToken(result.data.jwtToken);
        setMfaEmail(result.data.email || '');
        setPendingPassword(password);
        return {
          ok: true,
          mfaRequired: true,
          email: result.data.email,
          expiresInMinutes: result.data.expiresInMinutes,
        };
      }

      savePortalSession(PORTAL, result.data);
      setSession(loadPortalSession(PORTAL));
      logSessionIdentity(PORTAL, 'login');
      const mustChangePassword = !!result.data?.user?.mustChangePassword;
      setPendingPassword(mustChangePassword ? password : '');
      return { ok: true, mustChangePassword };
    } finally {
      setLoggingIn(false);
    }
  }, []);

  /** MFA step two. On success this is the point the session first exists. */
  const verifyOtp = useCallback(
    async (otp) => {
      if (!preAuthToken) return { ok: false, message: 'Start again from the sign-in screen.' };
      if (!/^\d{6}$/.test(String(otp || '').trim())) {
        return { ok: false, message: 'Enter the 6-digit code.' };
      }

      setLoggingIn(true);
      try {
        const result = await verifyPortalOtpAPI(PORTAL, preAuthToken, otp);
        if (!result.ok) return result;

        savePortalSession(PORTAL, result.data);
        setSession(loadPortalSession(PORTAL));
        logSessionIdentity(PORTAL, 'MFA verify');
        // Spent — holding it would leave a half-authenticated credential around.
        setPreAuthToken('');
        setMfaEmail('');
        const mustChangePassword = !!result.data?.user?.mustChangePassword;
        // `pendingPassword` (captured at step one) survives only if rotation needs it.
        if (!mustChangePassword) setPendingPassword('');
        return { ok: true, mustChangePassword };
      } finally {
        setLoggingIn(false);
      }
    },
    [preAuthToken],
  );

  /** Send another code. The previous one stops working. */
  const resendOtp = useCallback(async () => {
    if (!preAuthToken) return { ok: false, message: 'Start again from the sign-in screen.' };
    return await resendPortalOtpAPI(PORTAL, preAuthToken);
  }, [preAuthToken]);

  /** Abandon a half-finished sign-in (the code screen's "back"). */
  const cancelMfa = useCallback(() => {
    setPreAuthToken('');
    setMfaEmail('');
  }, []);

  /**
   * First-login rotation. The server returns no new token, so the existing one
   * stays valid and only the cached flag is cleared.
   *
   * The current password comes from `pendingPassword` — captured at sign-in —
   * rather than the screen asking for it a second time. Missing it means the tab
   * was reloaded (memory-only state), so the caller is told to send the user
   * back to sign in instead.
   */
  const changePassword = useCallback(
    async (newPassword) => {
      const token = session?.jwtToken;
      if (!token || !pendingPassword) {
        return { ok: false, message: 'Sign in again to continue.', needsReauth: true };
      }

      const result = await changePasswordAPI(token, pendingPassword, newPassword);
      if (!result.ok) return result;

      const next = { ...session, user: { ...session.user, mustChangePassword: false } };
      savePortalSession(PORTAL, next);
      setSession(next);
      setPendingPassword('');
      return { ok: true };
    },
    [session, pendingPassword],
  );

  /**
   * Sign out. Revokes the refresh family SERVER-side first (S-204) — clearing
   * local storage alone would leave the session usable by anyone holding the
   * refresh token. The local clear happens regardless of the call's outcome, so
   * a network failure cannot trap the user in a signed-in shell.
   */
  /**
   * The transport signs out a session the server has rejected, and this is how the
   * shell hears about it. Only the in-memory copy is dropped — the stored one is
   * already gone by the time this runs, and there is no refresh family left to
   * revoke — which flips `isAuthenticated` and lets ProtectedRoute redirect.
   *
   * Registered once for the provider's lifetime, so it cannot be lost when the
   * session changes.
   */
  useEffect(() => onSessionExpired(PORTAL, () => setSession(null)), []);

  /**
   * Print the rehydrated identity once per mount.
   *
   * The two call sites above only fire at the moment of sign-in, so without this
   * the only way to see which tenant a tab is scoped to would be to sign out and
   * back in — which is exactly the thing you cannot do when the question is
   * "what is this already-open session acting as?". A plain reload answers it.
   *
   * Guarded on a stored token because this provider mounts on the LOGIN screen
   * too, where there is nothing to report yet.
   */
  useEffect(() => {
    if (loadPortalSession(PORTAL)?.jwtToken) logSessionIdentity(PORTAL, 'rehydrated session');
  }, []);

  /**
   * A refresh rotated the tokens in storage; re-read them so `accessJWT` stops
   * being the superseded one. Nothing else in the session changes — the refresh
   * only replaces the token pair — so this is a re-read, not a re-fetch.
   *
   * Registered once for the provider's lifetime, alongside the sign-out handler
   * above and for the same reason: a rotation can land while no particular screen
   * is mounted.
   */
  useEffect(() => onSessionRefreshed(PORTAL, () => setSession(loadPortalSession(PORTAL))), []);

  /**
   * Rotate the access token on a TIMER while signed in.
   *
   * Every other refresh here is lazy — it happens because the portal is about to
   * make a call. That covers every ordinary screen, but not `/admin/travel/*`:
   * there the operator works inside the embedded traveller app, whose requests go
   * straight to app-backend and never pass through `postToBackend`, while the
   * portal itself issues nothing. The token lapsed with nothing to renew it (the
   * frame cannot — it only self-renews a token carrying `scope: "embed"`), and
   * every call from the frame started answering FAST_JWT_EXPIRED.
   *
   * Keyed on WHETHER there is a session, not on the token itself: the loop
   * re-reads the stored expiry on each tick, so a rotation must not tear it down
   * and rebuild it. See `keepSessionFresh`.
   */
  const signedIn = !!session?.jwtToken;
  useEffect(() => {
    if (!signedIn) return undefined;
    return keepSessionFresh(PORTAL);
  }, [signedIn]);

  const logout = useCallback(async () => {
    const refreshToken = session?.refreshToken;
    if (refreshToken) {
      try {
        await logoutAPI(PORTAL, refreshToken);
      } catch {
        /* best effort — the local clear below still happens */
      }
    }
    clearPortalSession(PORTAL);
    /**
     * The acting-partner selection goes with the session, not after it.
     *
     * It lives in its own sessionStorage key rather than inside the session
     * blob, so `clearPortalSession` never touched it — and this provider's
     * unmount does not clear it either. A logout therefore left the next
     * sign-in restoring a partner nobody chose, with the chooser skipped
     * entirely. See purgeActingPartnerStorage.
     */
    purgeActingPartnerStorage();
    setSession(null);
    setPendingPassword('');
  }, [session]);

  const value = useMemo(() => {
    const permissions = session?.permissions || [];
    return {
      // `user` keeps the shape the shell already reads (name/email), with the
      // role name taken from the granted role rather than hardcoded.
      user: session?.user
        ? { ...session.user, role: session?.role?.name || '', avatar: null }
        : null,
      partner: session?.partner || null,
      branches: session?.branches || [],
      role: session?.role || null,
      permissions,
      modules: session?.modules || [],
      // Affordance helpers — what the UI shows. NOT an authorization boundary;
      // the server enforces every protected route independently.
      perms: permissionChecker(permissions),
      isAuthenticated: !!session?.jwtToken,
      mustChangePassword: !!session?.user?.mustChangePassword,
      accessJWT: session?.jwtToken || '',
      /**
       * Which `user` row the embedded traveller app books under:
       * `{ partnerUserId, agentUserId, channel, defaultUserId }`.
       *
       * The portal's own `partner_users` id does NOT work there — it resolves to
       * no `user` row and fails silently rather than loudly. See app-backend
       * service/travellerIdentity.ts.
       */
      travellerIdentity: session?.travellerIdentity || null,
      loggingIn,
      login,
      logout,
      changePassword,
      // MFA step two. `mfaEmail` is only for telling the user where the code went.
      verifyOtp,
      resendOtp,
      cancelMfa,
      mfaEmail,
      awaitingOtp: !!preAuthToken,
    };
  }, [session, loggingIn, login, logout, changePassword, verifyOtp, resendOtp, cancelMfa, mfaEmail, preAuthToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
